import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { cn } from '@/lib/cn'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchUserStrikes, issueStrike, type AdminUserStrikes } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { RelativeTime } from '@/components/DataDisplay'
import { ErrorState, PermissionDenied, Skeleton } from '@/components/States'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'

function sevClass(n: number): string {
  if (n >= 4) return 'text-danger'
  if (n === 3) return 'text-warning'
  return 'text-success'
}

/**
 * A3.18 — Strike va cheklovlar tarixi (foydalanuvchi). GET /admin/users/:id/strikes
 * + POST /admin/users/:id/strike (backend `0252c93`). Aktiv ballar = amaldagi
 * strayklar og'irligining yig'indisi (progressiv-jazo qarori uchun).
 */
export function UserStrikes() {
  const t = useT()
  const { can } = useAuth()
  const { toast } = useToast()
  const { id = '' } = useParams()
  const [data, setData] = useState<AdminUserStrikes | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  const [severity, setSeverity] = useState(1)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    const ac = new AbortController()
    setLoading(true)
    setError(null)
    fetchUserStrikes(Number(id), ac.signal)
      .then((d) => setData(d))
      .catch((e: unknown) => {
        if (e instanceof DOMException && e.name === 'AbortError') return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => setLoading(false))
    return () => ac.abort()
  }, [id])

  useEffect(() => load(), [load, reload])

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />

  const submit = async () => {
    if (reason.trim().length < 3) return
    setBusy(true)
    try {
      await issueStrike(Number(id), severity, reason.trim())
      setReason('')
      toast(t.strikes.issued, { tone: 'success' })
      setReload((n) => n + 1)
    } catch (e) {
      toast(t.strikes.issueFailed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader
        title={t.strikes.title}
        screenId="A3.18"
        actions={
          <Link
            to={`/users/${id}`}
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.users.cardTitle}
          </Link>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading ? (
          <div className="max-w-xl space-y-2">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : (
          <div className="max-w-xl space-y-3">
            <div className="inline-flex items-center gap-3 rounded-lg border border-border-default bg-surface-2 px-4 py-2.5">
              <span className="text-xs text-tertiary">{t.strikes.activePoints}</span>
              <span className={cn('font-mono text-xl tabular', (data?.active_points ?? 0) >= 4 ? 'text-danger' : 'text-primary')}>
                {data?.active_points ?? 0}
              </span>
            </div>

            {can(PERMISSIONS.USERS_BAN) && (
              <div className="rounded-lg border border-border-default bg-surface-2 p-3">
                <div className="mb-2 text-sm font-medium text-secondary">{t.strikes.issue}</div>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={severity}
                    onChange={(e) => setSeverity(Number(e.target.value))}
                    className="rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary"
                  >
                    {[1, 2, 3, 4, 5].map((s) => (
                      <option key={s} value={s}>
                        {t.strikes.severity} {s}
                      </option>
                    ))}
                  </select>
                  <input
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={t.mod.reason}
                    className="min-w-40 flex-1 rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-sm text-primary"
                  />
                  <button
                    disabled={busy || reason.trim().length < 3}
                    onClick={submit}
                    className="rounded-md border border-danger bg-danger-subtle px-3 py-1.5 text-sm text-danger hover:bg-danger hover:text-white disabled:opacity-50"
                  >
                    {t.strikes.issueBtn}
                  </button>
                </div>
              </div>
            )}

            {!data?.strikes.length ? (
              <div className="rounded-lg border border-border-default bg-surface-2 p-6 text-center text-sm text-tertiary">
                {t.strikes.none}
              </div>
            ) : (
              <div className="overflow-hidden rounded-md border border-border-default">
                {data.strikes.map((s, i) => (
                  <div key={s.id} className={cn('flex items-center gap-3 px-3 py-2 text-sm', i > 0 && 'border-t border-border-subtle')}>
                    <span className={cn('w-6 shrink-0 text-center font-mono font-medium', sevClass(s.severity))}>{s.severity}</span>
                    <span className="min-w-0 flex-1 truncate text-primary">{s.reason}</span>
                    {s.report_id ? (
                      <Link to={`/moderation/${s.report_id}`} className="shrink-0 text-xs text-accent hover:underline">
                        #{s.report_id}
                      </Link>
                    ) : null}
                    <span className={cn('shrink-0 text-xs', s.active ? 'text-success' : 'text-tertiary')}>
                      {s.active ? t.strikes.active : t.strikes.expired}
                    </span>
                    <span className="shrink-0 text-xs text-tertiary">
                      <RelativeTime value={s.created_at} />
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}
